import { FC, useCallback, useContext, useMemo } from 'react';
import { Currency } from 'common/types/format';
import { CustomSelect } from 'components';
import { CustomSelectProps, MenuPosition, OptionType } from 'components/CustomSelect/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withTooltip from 'components/WithTooltip';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getObjectValue, getStringValue } from 'utilities';

interface ObjectOptions {
  displayKey: string;
  valueKey: string;
}

interface HandleOptionsNumberFormatParams {
  options: any[];
  format: any;
  currency: Currency;
  value: any;
}

interface GridSelectProps extends Pick<
  CustomSelectProps,
  | 'alignMenuToCellTop'
  | 'extraStyles'
  | 'matchFrom'
  | 'menuBoundaryMaxWidth'
  | 'menuBoundaryPadding'
  | 'menuBoundarySelector'
> {
  cell: ICell;
  options: OptionType[];
  enumerated?: boolean;
  objectOptions?: ObjectOptions;
  useHandleChange?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  menuPosition?: MenuPosition;
  handleOptionsNumberFormat?: (params: HandleOptionsNumberFormatParams) => OptionType;
  onCommit?: () => void;
  onRevert?: () => void;
  usePortal?: boolean;
  optionsTextAlign?: 'left' | 'right' | 'center';
  isMulti?: boolean;
}

const GridSelect: FC<GridSelectProps> = ({
  cell,
  enumerated,
  extraStyles,
  handleOptionsNumberFormat,
  isMulti = false,
  matchFrom,
  menuPlacement,
  menuPosition,
  objectOptions,
  options,
  useHandleChange = true,
  ...restProps
}) => {
  const { format, onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const currency = format?.currency;

  const objectList = useMemo(() => {
    if (handleOptionsNumberFormat) {
      return handleOptionsNumberFormat({
        options,
        format: cell.format,
        currency,
        value: cell.value,
      });
    }
    if (!objectOptions) {
      return options;
    }
    return options.map((option: any) => option[objectOptions.displayKey]);
  }, [options, objectOptions, cell, handleOptionsNumberFormat, currency]);

  const objectMap = useMemo(() => {
    if (objectOptions) {
      return options.reduce<Record<string, any>>((sofar, next) => {
        // eslint-disable-next-line no-param-reassign
        sofar[next[objectOptions.valueKey]] = next[objectOptions.displayKey];
        return sofar;
      }, {});
    }
    return undefined;
  }, [options, objectOptions]);

  const { onCommit, onRevert, optionsTextAlign, usePortal = false } = restProps;

  const getValueIndex = (value: any): number => options.findIndex(option => option === value);

  let cellValue: any = enumerated ? getValueIndex(cell.value) : cell.value;
  if (objectMap && cellValue) {
    cellValue = objectMap[cellValue];
  }

  const handleChange = useCallback(
    (value: any): void => {
      if (!useHandleChange) {
        return;
      }
      if (isMulti && Array.isArray(value)) {
        let newValues: any[] = [];
        if (objectOptions) {
          newValues = value.map((v: any) => {
            const idx = Number(v);
            return options[idx][objectOptions.valueKey as keyof OptionType];
          });
        } else if (options?.length && typeof options[0] === 'object') {
          newValues = value.map((v: any) => {
            const opt = options.find(o => String(o.value) === String(v));
            return opt ? (opt.value as any) : getStringValue(v);
          });
        } else {
          newValues = value.map((v: any) => getStringValue(v));
        }
        onCellsChanged([{ cell, value: newValues }]);
        return;
      }
      const newValue = objectOptions ? options[value][objectOptions.valueKey] : getStringValue(value);
      onCellsChanged([{ cell, value: newValue }]);
    },
    [cell, objectOptions, onCellsChanged, options, useHandleChange, isMulti]
  );

  return (
    <CustomSelect
      className={cell.alias}
      disabled={cell.readOnly}
      enumerated={enumerated}
      extraStyles={{
        ...getObjectValue(extraStyles),
        ...(optionsTextAlign && {
          option: (base, state) => {
            // If the external "option" style is a function (from extraStyles), execute it to
            // get the merged base styles. Otherwise, fall back to the default base styles
            const externalOptionFn = getObjectValue(extraStyles)?.option;
            const merged = typeof externalOptionFn === 'function' ? externalOptionFn(base, state) : base;

            return {
              padding: '6.5px 12px',
              textAlign: optionsTextAlign,
              ...merged,
            };
          },
        }),
      }}
      id={`${cell.alias}-${cell.key}`}
      isMulti={isMulti}
      alignMenuToCellTop={restProps.alignMenuToCellTop}
      matchFrom={matchFrom}
      menuBoundaryMaxWidth={restProps.menuBoundaryMaxWidth}
      menuBoundaryPadding={restProps.menuBoundaryPadding}
      menuBoundarySelector={restProps.menuBoundarySelector}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      onChange={handleChange}
      onCommit={onCommit}
      onEmptyValue="Select Option"
      onRevert={onRevert}
      options={objectList}
      usePortal={usePortal}
      value={cellValue}
    />
  );
};

export default withTooltip(GridSelect);
